import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';

const RequestHistory = ({ 
  requests, 
  onSelectRequest, 
  selectedRequest, 
  onAcceptRequest, 
  onRejectRequest, 
  userRole 
}) => {
  const { t } = useTranslation();
  const [sortBy, setSortBy] = useState('submittedDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [viewMode, setViewMode] = useState('list'); // list, card

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-border';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return 'Clock';
      case 'approved':
        return 'CheckCircle';
      case 'rejected':
        return 'XCircle';
      default:
        return 'FileText';
    }
  };

  const getTypeLabel = (type) => {
    const types = {
      'missing-clockout': 'Missing Clock Out',
      'incorrect-time': 'Incorrect Time',
      'missing-entry': 'Missing Entry',
      'break-time': 'Break Time Correction'
    };
    return t(types?.[type] || type);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const sortRequests = (requests) => {
    return [...requests]?.sort((a, b) => {
      let aValue = a?.[sortBy];
      let bValue = b?.[sortBy];

      if (sortBy === 'submittedDate' || sortBy === 'date') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const sortedRequests = sortRequests(requests);

  const getSortIcon = (field) => {
    if (sortBy !== field) return 'ArrowUpDown';
    return sortOrder === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  const getRequesterName = (request) => {
    const submitted = request?.workflow?.find?.(s => s?.stage === 'submitted');
    const name = submitted?.actor;
    const label = name && String(name).trim().length > 0 ? String(name).trim() : null;
    if (!label) return null;
    if (label.toLowerCase() === 'employee') return null;
    return label;
  };

  const RequestCard = ({ request }) => (
    <div
      className={`bg-card border rounded-xl p-6 transition-all duration-200 hover:shadow-md ${
        selectedRequest?.id === request?.id ? 'border-orange-500 shadow-md' : 'border-border hover:border-orange-300'
      }`}
    >
      <div className="flex items-start justify-between mb-4">
        <div 
          className="flex items-center space-x-3 cursor-pointer flex-1"
          onClick={() => onSelectRequest?.(request)}
        >
          <div className="w-10 h-10 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-lg flex items-center justify-center">
            <Icon name={getStatusIcon(request?.status)} size={20} className="text-orange-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">{request?.id}</h3>
            <p className="text-sm text-muted-foreground">{getTypeLabel(request?.type)}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(request?.status)}`}>
            {request?.status?.toUpperCase()}
          </span>
          
          {/* Accept/Reject Actions for pending requests */}
          {request?.status === 'pending' && (userRole === 'manager' || userRole === 'hr') && (
            <div className="flex items-center space-x-1">
              <button
                onClick={(e) => {
                  e?.stopPropagation();
                  onAcceptRequest?.(request);
                }}
                className="p-1.5 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                title={t('Accept Request')}
              >
                <Icon name="Check" size={16} />
              </button>
              <button
                onClick={(e) => {
                  e?.stopPropagation();
                  onRejectRequest?.(request, t('Rejected from quick action'));
                }}
                className="p-1.5 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                title={t('Reject Request')}
              >
                <Icon name="X" size={16} />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center space-x-4 text-sm">
          <div className="flex items-center space-x-1">
            <Icon name="Calendar" size={14} className="text-muted-foreground" />
            <span className="text-muted-foreground">{t('Date:')}</span>
            <span className="font-medium text-foreground">{formatDate(request?.date)}</span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="Clock" size={14} className="text-muted-foreground" />
            <span className="text-muted-foreground">{t('Submitted:')}</span>
            <span className="font-medium text-foreground">{formatDate(request?.submittedDate)}</span>
          </div>
        </div>

        {(getRequesterName(request) || request?.approver) && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            {getRequesterName(request) && (
              <div className="flex items-center space-x-1">
                <Icon name="User" size={14} className="text-muted-foreground" />
                <span className="text-muted-foreground">{t('Employee:')}</span>
                <span className="font-medium text-foreground">{getRequesterName(request)}</span>
              </div>
            )}
            {request?.approver && (
              <div className="flex items-center space-x-1">
                <Icon name="UserCheck" size={14} className="text-muted-foreground" />
                <span className="text-muted-foreground">{t('Approver:')}</span>
                <span className="font-medium text-foreground">{request?.approver}</span>
              </div>
            )}
          </div>
        )}

        {/* Time Changes */}
        {(request?.requestedCheckIn || request?.requestedCheckOut) && (
          <div className="bg-muted/30 rounded-lg p-3 space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase">{t('Time Changes')}</p>
            {request?.requestedCheckIn && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{t('Check In:')}</span>
                <span className="font-mono">
                  {request?.currentCheckIn ? (
                    <>
                      <span className="text-red-600 line-through">{request?.currentCheckIn}</span>
                      <span className="mx-2">→</span>
                      <span className="text-green-600">{request?.requestedCheckIn}</span>
                    </>
                  ) : (
                    <span className="text-green-600">{request?.requestedCheckIn}</span>
                  )}
                </span>
              </div>
            )}
            {request?.requestedCheckOut && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{t('Check Out:')}</span>
                <span className="font-mono">
                  {request?.currentCheckOut ? (
                    <>
                      <span className="text-red-600 line-through">{request?.currentCheckOut}</span>
                      <span className="mx-2">→</span>
                      <span className="text-green-600">{request?.requestedCheckOut}</span>
                    </>
                  ) : (
                    <span className="text-green-600">{request?.requestedCheckOut}</span>
                  )}
                </span>
              </div>
            )}
          </div>
        )}

        <div className="pt-2 border-t border-border cursor-pointer" onClick={() => onSelectRequest?.(request)}>
          <p className="text-sm text-muted-foreground line-clamp-2">
            {request?.reason}
          </p>
        </div>

        {request?.attachments?.length > 0 && (
          <div className="flex items-center space-x-2 text-xs text-muted-foreground">
            <Icon name="Paperclip" size={12} />
            <span>{request?.attachments?.length} {t('attachment(s)')}</span>
          </div>
        )}
      </div>
    </div>
  );

  const RequestRow = ({ request }) => (
    <tr
      className={`hover:bg-muted/30 transition-colors ${
        selectedRequest?.id === request?.id ? 'bg-orange-50' : ''
      }`}
    >
      <td className="px-4 py-4 whitespace-nowrap cursor-pointer" onClick={() => onSelectRequest?.(request)}>
        <div className="flex items-center space-x-3">
          <Icon name={getStatusIcon(request?.status)} size={16} className="text-muted-foreground" />
          <span className="font-medium text-foreground">{request?.id}</span>
        </div>
      </td>
      <td className="px-4 py-4 whitespace-nowrap cursor-pointer" onClick={() => onSelectRequest?.(request)}>
        <span className="text-sm text-foreground">{formatDate(request?.date)}</span>
      </td>
      <td className="px-4 py-4 cursor-pointer" onClick={() => onSelectRequest?.(request)}>
        <span className="text-sm text-foreground">{getTypeLabel(request?.type)}</span>
      </td>
      <td className="px-4 py-4 cursor-pointer" onClick={() => onSelectRequest?.(request)}>
        <div className="text-sm space-y-1">
          {request?.requestedCheckIn && (
            <div className="flex items-center space-x-2">
              <span className="text-muted-foreground">{t('In:')}</span>
              <span className="font-mono text-foreground">{request?.requestedCheckIn}</span>
            </div>
          )}
          {request?.requestedCheckOut && (
            <div className="flex items-center space-x-2">
              <span className="text-muted-foreground">{t('Out:')}</span>
              <span className="font-mono text-foreground">{request?.requestedCheckOut}</span>
            </div>
          )}
          {getRequesterName(request) && (
            <div className="flex items-center space-x-2">
              <span className="text-muted-foreground">{t('Employee:')}</span>
              <span className="text-foreground">{getRequesterName(request)}</span>
            </div>
          )}
        </div>
      </td>
      <td className="px-4 py-4 whitespace-nowrap cursor-pointer" onClick={() => onSelectRequest?.(request)}>
        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(request?.status)}`}>
          {request?.status?.toUpperCase()}
        </span>
      </td>
      <td className="px-4 py-4 whitespace-nowrap cursor-pointer" onClick={() => onSelectRequest?.(request)}>
        <span className="text-sm text-muted-foreground">{formatDate(request?.submittedDate)}</span>
      </td>
      <td className="px-4 py-4 whitespace-nowrap">
        <div className="flex items-center justify-between">
          {request?.attachments?.length > 0 && (
            <div className="flex items-center space-x-1 text-muted-foreground cursor-pointer" onClick={() => onSelectRequest?.(request)}>
              <Icon name="Paperclip" size={14} />
              <span className="text-xs">{request?.attachments?.length}</span>
            </div>
          )}
          
          {/* Accept/Reject Actions for pending requests */}
          {request?.status === 'pending' && (userRole === 'manager' || userRole === 'hr') && (
            <div className="flex items-center space-x-1 ml-2">
              <button
                onClick={(e) => {
                  e?.stopPropagation();
                  onAcceptRequest?.(request?.id);
                }}
                className="p-1.5 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                title={t('Accept Request')}
              >
                <Icon name="Check" size={16} />
              </button>
              <button
                onClick={(e) => {
                  e?.stopPropagation();
                  onRejectRequest?.(request?.id, t('Rejected from quick action'));
                }}
                className="p-1.5 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                title={t('Reject Request')}
              >
                <Icon name="X" size={16} />
              </button>
            </div>
          )}
        </div>
      </td>
    </tr>
  );

  if (!requests?.length) {
    return (
      <div className="text-center py-12 bg-card border border-border rounded-xl">
        <Icon name="FileX" size={48} className="mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">{t('No Correction Requests')}</h3>
        <p className="text-muted-foreground mb-6">
          {t("You haven't submitted any attendance correction requests yet.")}
        </p>
        <p className="text-sm text-muted-foreground">
          {t('Use the "Submit Request" tab to create your first correction request.')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* View Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-muted-foreground">{t('View:')}</span>
          <div className="flex items-center bg-muted/30 rounded-lg p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                viewMode === 'list' ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name="List" size={14} className="mr-1" />
              {t('List')}
            </button>
            <button
              onClick={() => setViewMode('card')}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                viewMode === 'card' ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name="Grid3X3" size={14} className="mr-1" />
              {t('Cards')}
            </button>
          </div>
        </div>

        <div className="text-sm text-muted-foreground">
          {requests?.length} {requests?.length === 1 ? t('request') : t('requests')}
        </div>
      </div>

      {/* Content */}
      {viewMode === 'card' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedRequests?.map(request => (
            <RequestCard key={request?.id} request={request} />
          ))}
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/30">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort('id')}
                      className="flex items-center space-x-1 text-sm font-semibold text-foreground hover:text-orange-600 transition-colors"
                    >
                      <span>{t('Request ID')}</span>
                      <Icon name={getSortIcon('id')} size={14} />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort('date')}
                      className="flex items-center space-x-1 text-sm font-semibold text-foreground hover:text-orange-600 transition-colors"
                    >
                      <span>{t('Date')}</span>
                      <Icon name={getSortIcon('date')} size={14} />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort('type')}
                      className="flex items-center space-x-1 text-sm font-semibold text-foreground hover:text-orange-600 transition-colors"
                    >
                      <span>{t('Type')}</span>
                      <Icon name={getSortIcon('type')} size={14} />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <span className="text-sm font-semibold text-foreground">{t('Requested Times')}</span>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort('status')}
                      className="flex items-center space-x-1 text-sm font-semibold text-foreground hover:text-orange-600 transition-colors"
                    >
                      <span>{t('Status')}</span>
                      <Icon name={getSortIcon('status')} size={14} />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort('submittedDate')}
                      className="flex items-center space-x-1 text-sm font-semibold text-foreground hover:text-orange-600 transition-colors"
                    >
                      <span>{t('Submitted')}</span>
                      <Icon name={getSortIcon('submittedDate')} size={14} />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <span className="text-sm font-semibold text-foreground">{t('Actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {sortedRequests?.map(request => (
                  <RequestRow key={request?.id} request={request} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestHistory;